import { apis, getMockApis } from '../../api/api';
import React, { createContext, useEffect, useState } from 'react';
import GenericError from '../error-screen/generic-error/GenericError';
import { LoadingIndicator } from '@monash/portal-react';

export const MockContext = createContext();

const API_NAMES = Object.keys(apis);

/**
 * Sets an object which tracks which APIs are supposed to be mocked.
 *
 * e.g.
 * {
 *    api1: true/false,
 *    api2: true/false,
 *    ...
 * }
 * @param baseValue true if all APIs are mocked, false if all APIs are not mocked
 * @returns object of the form [apiName: string]: boolean
 */
const initMockApiConfig = (baseValue = false) => {
  const mockApiConfig = {};
  for (const apiName of API_NAMES) {
    mockApiConfig[apiName] = Boolean(baseValue);
  }
  return mockApiConfig;
};

const MockProvider = ({ children, useMocks: useMocksProp }) => {
  const [mockUserId, setMockUserId] = useState();
  const [mockApisSource, setMockApisSource] = useState();
  const [isLoadingMocks, setIsLoadingMocks] = useState(false);
  const [mocksError, setMocksError] = useState(false);
  const [mockApiConfig, setMockApiConfig] = useState(initMockApiConfig());

  const getHardCodedMockApis = async () => {
    const { mocks } = await import('../../api/mocks');
    return mocks;
  };

  const getMockApisFromStorage = async (uid) => {
    try {
      const resp = await getMockApis(uid);
      setMocksError(false);
      return resp;
    } catch (error) {
      console.error('Failed to get mock apis from storage. ', error);
      setMocksError(true);
    }
  };

  const getMockApisSource = async () => {
    setIsLoadingMocks(true);

    if (useMocksProp) {
      const defaultMocks = await getHardCodedMockApis();
      setMockApisSource(defaultMocks);
    } else if (mockUserId) {
      const mockApisFromStorage = await getMockApisFromStorage(mockUserId);
      setMockApisSource(mockApisFromStorage);
    } else {
      setMockApisSource(null);
    }

    setIsLoadingMocks(false);
  };

  useEffect(() => {
    getMockApisSource();
  }, [useMocksProp, mockUserId]);

  const generateActiveMockApis = () => {
    if (!mockApisSource) return {};

    // If we have a valid source, generate apis that should be mocked
    const activeMockApis = {};
    for (const apiName of API_NAMES) {
      const shouldBeMocked = mockApiConfig[apiName] || useMocksProp;
      if (shouldBeMocked) {
        activeMockApis[apiName] = async () => {
          if (!(apiName in mockApisSource)) {
            console.warn(
              `${apiName} was not found in mock API data source, returning undefined`,
            );
          }

          return mockApisSource[apiName];
        };
      }
    }
    return activeMockApis;
  };

  const activeMockApis = generateActiveMockApis();
  const mockApisAreActive = Object.keys(activeMockApis).length > 0;

  /**
   * Update which APIs are supposed to be mocked.
   *
   * @param configUpdate object representing changes to the mock API config
   * e.g.
   * {
   *    api1: true,
   *    api2: false,
   * }
   *
   * means api1 should be mocked, whereas api2 should not be mocked
   *
   * @param baseValue true/false/null representing the rest of the mock API statuses that
   * were NOT specified in configUpdate, i.e.
   * if null, don't update any other API mock statuses
   * if true, mock all other APIs
   * if false, do NOT mock all other APIs
   */
  const updateMockApiConfig = ({ configUpdate, baseValue = null } = {}) => {
    const validatedConfigUpdate = { ...configUpdate };

    for (const apiName of Object.keys(configUpdate)) {
      if (!API_NAMES.includes(apiName)) {
        console.warn(
          `${apiName} not recognised in list of known APIs, skipping mock config update.`,
        );
        delete validatedConfigUpdate[apiName];
      }
    }

    if (baseValue === null) {
      setMockApiConfig((prevMockApiConfig) => {
        return {
          ...prevMockApiConfig,
          ...validatedConfigUpdate,
        };
      });
    } else {
      const newMockApiConfig = initMockApiConfig(baseValue);
      setMockApiConfig({
        ...newMockApiConfig,
        ...validatedConfigUpdate,
      });
    }
  };

  const isApiMocked = (apiName) => {
    return apiName in activeMockApis;
  };

  const callMockApi = async (apiName) => {
    const mockApi = activeMockApis[apiName];
    return await mockApi();
  };

  const renderContent = () => {
    if (isLoadingMocks) {
      return <LoadingIndicator />;
    }

    if (mocksError) {
      return <GenericError />;
    }

    return children;
  };

  return (
    <MockContext.Provider
      value={{
        mockUserId,
        setMockUserId,
        activeMockApis,
        mockApisAreActive,
        updateMockApiConfig,
        isApiMocked,
        callMockApi,
      }}
    >
      {renderContent()}
    </MockContext.Provider>
  );
};

export default MockProvider;
